import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { toast } from 'react-toastify';
import api from '../../services/api';
import apiErrorFormatter from '../../services/apiErrorFormatter';

import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';

import AddIcon from '@mui/icons-material/Add';

function DialogAddLayoutItem(props) {
  const { t } = useTranslation('maintenance');

  const { open, onClose, onValidate, type, id } = props;

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const [name, setName] = useState('');

  const validate = async () => {
    setLoading(true);
    setErrors([]);
    await api
      .post('/maintenance/add-layout-item', {
        type: type,
        id: id,
        name: name,
      })
      .then((res) => {
        toast.success(t('common:saved_successfully'));
        onValidate(type);
      })
      .catch((err) => {
        setErrors(apiErrorFormatter(err));
      });
    setLoading(false);
  };

  const dialogExited = () => {
    setLoading(false);
    setErrors([]);
    setName('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      fullWidth
      TransitionProps={{
        onExited: dialogExited,
      }}
    >
      <DialogTitle id="dialog-title">
        {type ? t('add_new_' + type) : t('common:add')}
      </DialogTitle>
      <DialogContent id="dialog-description">
        <Box mt={1}>
          <TextField
            required
            fullWidth
            id="name"
            label={t('common:name')}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
            }}
            disabled={loading}
          />
        </Box>
        {errors.length > 0 && (
          <Box>
            {errors
              .slice()
              .reverse()
              .map((error) => (
                <Box key={error} mt={1}>
                  <Alert variant="outlined" severity="error">
                    {t(error)}
                  </Alert>
                </Box>
              ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{ mb: 2, mx: 2, display: 'flex', justifyContent: 'space-between' }}
      >
        <Button
          variant="contained"
          sx={{
            width: '45%',
            px: 2,
            py: 2,
            color: 'primary.contrastText',
            backgroundColor: 'success.main',
            ':hover': {
              color: 'primary.contrastText',
              backgroundColor: 'success.dark',
            },
          }}
          onClick={validate}
          disabled={loading || !name || !name.trim()}
          startIcon={
            !loading ? (
              <AddIcon />
            ) : (
              <CircularProgress color="inherit" size={20} />
            )
          }
        >
          {t('common:add')}
        </Button>
        <Button
          variant="outlined"
          color="primary"
          sx={{ width: '45%', py: 2 }}
          onClick={onClose}
          disabled={loading}
          autoFocus
        >
          {t('common:cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

DialogAddLayoutItem.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
};

export default DialogAddLayoutItem;
